/**
 * Garment-type display helpers, shared across the styles feature so the
 * humaniser isn't re-implemented per screen (it was duplicated in the list +
 * detail). The canonical labels come from `styles.templates` at runtime; this
 * is the offline fallback for keys we only have as strings.
 */
import type { ComboboxGroup } from "@flint/ui";

/** Humanize a garment-type key for display (knit_tee → "Knit tee"). */
export function garmentTypeLabel(key: string): string {
  return key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

interface TemplateRow {
  key: string;
  label: string;
  category: string;
  styleCodePrefix: string;
}

/**
 * Convert the `styles.templates` response into `ComboboxGroup[]` grouped by
 * category, preserving the server-side category order.
 */
export function buildGarmentTypeGroups(templates: readonly TemplateRow[]): ComboboxGroup[] {
  const map = new Map<string, { label: string; options: Array<{ value: string; label: string }> }>();
  for (const t of templates) {
    let group = map.get(t.category);
    if (!group) {
      group = { label: categoryLabel(t.category), options: [] };
      map.set(t.category, group);
    }
    group.options.push({ value: t.key, label: t.label });
  }
  return [...map.values()];
}

/** Look up the style-code prefix for a garment type from templates data. */
export function styleCodePrefixFor(templates: readonly TemplateRow[], garmentType: string): string {
  return templates.find((t) => t.key === garmentType)?.styleCodePrefix ?? "STY";
}

/** Humanize a category key for group headings. */
function categoryLabel(category: string): string {
  const LABELS: Record<string, string> = {
    knit_tops: "Knit tops",
    woven_tops: "Woven tops",
    bottoms: "Bottoms",
    dresses_onepiece: "Dresses & one-piece",
    outerwear: "Outerwear",
    knitwear: "Knitwear",
    activewear: "Activewear",
    inner_loungewear: "Inner & loungewear",
    ethnicwear: "Ethnicwear (India)",
    kidswear: "Kidswear",
    accessories: "Accessories",
  };
  return LABELS[category] ?? category.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}
